/**
 * The pipeline board (W12) — the app's default view.
 *
 * One column per stage, with every deal as a card. Moving a card is the
 * instruction that drives everything else: dragging a deal into "Want to talk"
 * is how Andi tells the agent he wants a call, and the agent will not write to
 * anybody about a deal outside "Want to talk" / "Call scheduled".
 *
 * The two dead ends bracket the pipeline — "Rejected by us" on the left,
 * "Rejected by them" on the right, folded to a slim rail until opened — so a
 * killed deal is somewhere visible rather than hidden behind a filter, and which side ended it is recorded
 * rather than buried in the outcome text. Dropping a card in either takes it
 * out of the running (the server keeps `still_in_running` in step); dragging
 * it back out returns it to the pipeline.
 *
 * Deals have owners. A new deal lands unclaimed (a dashed "?" badge and a
 * Claim button); whoever claims it moves it from then on, and the agent
 * writes from their mailbox. Everyone sees every deal; only the owner (or an
 * admin) can drag one. The filter above the board narrows it to yours, the
 * unclaimed pool, or one person's.
 *
 * Cards are dragged between columns. The deal page carries the same stage as
 * a dropdown, which is the route on a phone or a keyboard, and the table
 * view's toggles move a deal too — the server keeps all three in step.
 *
 * Moves are optimistic: the card jumps immediately and rolls back with a
 * notice if the PATCH fails, because a board that lags every drag by a
 * round-trip feels broken.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { listAgentRuns, type AgentRun } from '../api/agent.js';
import {
  AlreadyClaimedError,
  AuthExpiredError,
  claimIm,
  imDownloadHref,
  isRejectedStage,
  listIms,
  patchIm,
  STAGES,
  STAGE_LABELS,
  type Im,
  type Stage,
} from '../api/ims.js';
import { OwnerBadge, useSession } from '../components/Session.js';

type LoadState = 'loading' | 'ready' | 'error';

const moneyFmt = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  notation: 'compact',
  maximumSignificantDigits: 3,
});

function money(v: string | null): string | null {
  if (v === null || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? v : moneyFmt.format(n);
}

function dealTitle(im: Im): string {
  return im.name ?? im.original_filename ?? `Deal #${im.opportunity_number}`;
}

function daysSince(iso: string): number {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

const whenFmt = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

function when(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : whenFmt.format(d);
}

/** How long a deal has sat in one column before it is worth noticing. */
const STALE_DAYS = 7;

/** Each stage's colour: the column's dot and the card's top edge. */
const STAGE_TONE: Record<Stage, { dot: string; edge: string }> = {
  rejected_by_us: { dot: 'bg-ink-500', edge: 'before:bg-ink-500/40' },
  teaser_received: { dot: 'bg-slate-300', edge: 'before:bg-slate-300/60' },
  want_im: { dot: 'bg-sky-400', edge: 'before:bg-sky-400/70' },
  im_received: { dot: 'bg-accent-400', edge: 'before:bg-accent-400/70' },
  want_to_talk: { dot: 'bg-fuchsia-400', edge: 'before:bg-fuchsia-400/70' },
  call_scheduled: { dot: 'bg-warn-400', edge: 'before:bg-warn-400/70' },
  call_had: { dot: 'bg-positive-400', edge: 'before:bg-positive-400/70' },
  offer_made: { dot: 'bg-emerald-300', edge: 'before:bg-emerald-300/70' },
  rejected_by_them: { dot: 'bg-ink-500', edge: 'before:bg-ink-500/40' },
};

/** Our rating out of 10, coloured so the strong ones stand out on the board. */
function Rating({ value }: { value: string | number | null }) {
  if (value === null) return null;
  const n = Number(value);
  const tone =
    n >= 7
      ? 'bg-positive-500/15 text-positive-400 ring-positive-500/25'
      : n >= 5
        ? 'bg-warn-500/10 text-warn-400 ring-warn-500/20'
        : 'bg-white/[0.04] text-ink-400 ring-white/10';
  return (
    <span
      className={`inline-flex h-6 min-w-[2.25rem] items-center justify-center rounded-md px-1.5 text-xs font-semibold tabular-nums ring-1 ring-inset ${tone}`}
      title="Our rating out of 10"
    >
      {n.toFixed(1)}
    </span>
  );
}

function Card({
  im,
  onOpen,
  dragging,
  onDragStart,
  onDragEnd,
  onClaim,
  claiming,
}: {
  im: Im;
  onOpen: () => void;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onClaim: () => void;
  claiming: boolean;
}) {
  const { canEdit, isMine, ownerName } = useSession();
  const editable = canEdit(im);
  const owner = ownerName(im.owner_email);
  const revenue = money(im.revenue);
  const ebitda = money(im.ebitda);
  const callAt = when(im.call_scheduled_at);
  const stale = daysSince(im.stage_changed_at);
  const dead = isRejectedStage(im.stage);
  const negative = (v: string | null) => v !== null && Number(v) < 0;

  return (
    <div
      draggable={editable}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', im.id);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      className={`group relative cursor-pointer overflow-hidden rounded-xl border border-white/[0.06] bg-ink-800/90 p-3 shadow-card transition before:absolute before:inset-x-0 before:top-0 before:h-0.5 ${
        STAGE_TONE[im.stage].edge
      } ${
        dragging
          ? 'opacity-40'
          : 'hover:-translate-y-0.5 hover:border-white/[0.12] hover:bg-ink-700/80 hover:shadow-lift'
      } ${dead ? 'opacity-75 hover:opacity-100' : ''}`}
    >
      <div className="flex items-start gap-2">
        <div
          className="line-clamp-2 min-w-0 flex-1 text-[13px] font-semibold leading-snug text-ink-100"
          title={dealTitle(im)}
        >
          {dealTitle(im)}
        </div>
        <OwnerBadge email={im.owner_email} name={owner} />
      </div>
      <div className="mt-1 truncate text-[11px] text-ink-400">
        <span className="tabular-nums">#{im.opportunity_number}</span>
        {im.counterparty_name ? ` · ${im.counterparty_name}` : ''}
      </div>

      {revenue || ebitda || im.initial_rating !== null ? (
        <dl className="mt-2.5 grid grid-cols-[1fr_1fr_auto] items-center gap-1.5 rounded-lg bg-black/20 py-1.5 pl-2 pr-1.5">
          <div className="min-w-0">
            <dt className="text-[10px] text-ink-500">Revenue</dt>
            <dd className="truncate text-xs font-medium tabular-nums text-ink-100">
              {revenue ?? '—'}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-[10px] text-ink-500">EBITDA</dt>
            <dd
              className={`truncate text-xs font-medium tabular-nums ${
                negative(im.ebitda) ? 'text-negative-500' : 'text-ink-100'
              }`}
            >
              {ebitda ?? '—'}
            </dd>
          </div>
          <Rating value={im.initial_rating} />
        </dl>
      ) : null}

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        {callAt ? (
          <span className="pill bg-positive-500/15 text-positive-400">
            <span aria-hidden>●</span> {callAt}
          </span>
        ) : null}
        {im.agent_paused_reason ? (
          <span className="pill bg-warn-500/15 text-warn-400" title={im.agent_paused_reason}>
            {isMine(im) || !owner ? 'Needs you' : `Needs ${owner}`}
          </span>
        ) : null}
        {im.document_kind === 'teaser' ? (
          <span className="pill bg-white/[0.05] text-ink-300">Teaser only</span>
        ) : null}
        {!im.agent_enabled ? (
          <span className="pill bg-white/[0.05] text-ink-400">Agent off</span>
        ) : null}
        {stale >= STALE_DAYS && !dead ? (
          <span className="pill bg-white/[0.05] text-ink-400" title="Days in this stage">
            {stale}d
          </span>
        ) : null}
        <span className="ml-auto flex items-center gap-1.5">
          {/* The document, one click from the board. stopPropagation keeps
              the download from also opening the deal page underneath it. */}
          <a
            href={imDownloadHref(im.id)}
            download
            onClick={(e) => e.stopPropagation()}
            title={`Download the ${im.document_kind === 'teaser' ? 'teaser' : 'IM'}`}
            className="pill bg-white/[0.05] text-ink-300 hover:bg-white/10 hover:text-ink-100"
          >
            PDF
          </a>
          {im.owner_email === null ? (
            <button
              type="button"
              disabled={claiming}
              onClick={(e) => {
                e.stopPropagation();
                onClaim();
              }}
              className="pill bg-accent-500 font-semibold text-white hover:bg-accent-400 disabled:opacity-50"
            >
              {claiming ? 'Claiming…' : 'Claim'}
            </button>
          ) : null}
        </span>
      </div>
    </div>
  );
}

type OwnerFilter = 'all' | 'mine' | 'unclaimed' | string;

const FILTER_KEY = 'dealflow.board.owner';

function readFilter(): OwnerFilter {
  try {
    return window.localStorage.getItem(FILTER_KEY) ?? 'all';
  } catch {
    return 'all';
  }
}

const OPEN_DEAD_KEY = 'dealflow.board.openRejected';

/** Which of the two rejected columns are unfolded (both folded by default). */
function readOpenDead(): Set<Stage> {
  try {
    const raw = window.localStorage.getItem(OPEN_DEAD_KEY);
    return new Set(raw ? (JSON.parse(raw) as Stage[]) : []);
  } catch {
    return new Set();
  }
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
        active
          ? 'bg-white/10 text-ink-100 shadow-sm'
          : 'text-ink-400 hover:bg-white/[0.04] hover:text-ink-100'
      }`}
    >
      {children}
    </button>
  );
}

function AgentStatus({ run }: { run: AgentRun | null }) {
  if (!run) {
    return <span className="text-ink-500">Agent has not run yet.</span>;
  }
  const at = when(run.started_at) ?? 'recently';
  if (run.status === 'failed') {
    return (
      <span className="text-negative-500" title={run.error ?? undefined}>
        Agent run failed {at}.
      </span>
    );
  }
  return (
    <span className="text-ink-300">
      Agent last ran {at} — reviewed {run.deals_reviewed}, sent {run.emails_sent}
      {run.stage_changes > 0 ? `, moved ${run.stage_changes}` : ''}.
    </span>
  );
}

export function BoardView() {
  const navigate = useNavigate();
  const { user, team, canEdit, isMine } = useSession();
  const [filter, setFilterState] = useState<OwnerFilter>(readFilter);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const setFilter = (next: OwnerFilter) => {
    setFilterState(next);
    try {
      window.localStorage.setItem(FILTER_KEY, next);
    } catch {
      /* private mode: the filter just won't stick */
    }
  };
  const [ims, setIms] = useState<Im[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [lastRun, setLastRun] = useState<AgentRun | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overStage, setOverStage] = useState<Stage | null>(null);
  const [openDead, setOpenDead] = useState<Set<Stage>>(readOpenDead);
  const toggleDead = (stage: Stage) => {
    setOpenDead((current) => {
      const next = new Set(current);
      if (next.has(stage)) next.delete(stage);
      else next.add(stage);
      try {
        window.localStorage.setItem(OPEN_DEAD_KEY, JSON.stringify([...next]));
      } catch {
        /* private mode: the columns just won't stay open */
      }
      return next;
    });
  };

  const load = useCallback(async () => {
    try {
      setIms(await listIms());
      setState('ready');
      setError(null);
    } catch (e) {
      if (e instanceof AuthExpiredError) {
        window.location.reload();
        return;
      }
      setState('error');
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
    // The run log is nice-to-have: a failure here must not break the board.
    void listAgentRuns(1)
      .then((runs) => setLastRun(runs[0] ?? null))
      .catch(() => setLastRun(null));
  }, [load]);

  const visible = useMemo(
    () =>
      ims.filter((im) => {
        if (filter === 'all') return true;
        if (filter === 'mine') return isMine(im);
        if (filter === 'unclaimed') return im.owner_email === null;
        return im.owner_email === filter;
      }),
    [ims, filter, isMine],
  );
  const unclaimedCount = useMemo(
    () => ims.filter((im) => im.owner_email === null && im.still_in_running).length,
    [ims],
  );

  const byStage = useMemo(() => {
    const map = new Map<Stage, Im[]>(STAGES.map((s) => [s, []]));
    for (const im of visible) {
      const bucket = map.get(im.stage);
      if (bucket) bucket.push(im);
      else map.get('im_received')?.push(im);
    }
    for (const [stage, list] of map) {
      // In the pipeline, the deal that has waited longest sits at the top —
      // it is the one at risk of being forgotten. In the two dead ends the
      // opposite is useful: the most recent rejection first, since older ones
      // are only ever history.
      const oldestFirst = !isRejectedStage(stage);
      list.sort((a, b) => {
        const d =
          new Date(a.stage_changed_at).getTime() - new Date(b.stage_changed_at).getTime();
        return oldestFirst ? d : -d;
      });
    }
    return map;
  }, [visible]);

  const claim = useCallback(async (im: Im) => {
    setClaimingId(im.id);
    try {
      const updated = await claimIm(im.id);
      setIms((rows) => rows.map((row) => (row.id === im.id ? updated : row)));
      setError(null);
    } catch (e) {
      if (e instanceof AuthExpiredError) {
        window.location.reload();
        return;
      }
      if (e instanceof AlreadyClaimedError && e.im) {
        const taken = e.im;
        setIms((rows) => rows.map((row) => (row.id === im.id ? taken : row)));
      }
      setError(`Couldn't claim ${dealTitle(im)}: ${(e as Error).message}`);
    } finally {
      setClaimingId(null);
    }
  }, []);

  const move = useCallback(
    async (im: Im, stage: Stage) => {
      if (im.stage === stage) return;
      if (!canEdit(im)) {
        setError(
          im.owner_email === null
            ? `Claim ${dealTitle(im)} before moving it.`
            : `${dealTitle(im)} belongs to someone else; only they (or an admin) can move it.`,
        );
        return;
      }
      const previous = ims;
      // Optimistic: move it now, put it back if the server disagrees.
      setIms((rows) =>
        rows.map((row) =>
          row.id === im.id
            ? { ...row, stage, stage_changed_at: new Date().toISOString() }
            : row,
        ),
      );
      try {
        const updated = await patchIm(im.id, { stage });
        setIms((rows) => rows.map((row) => (row.id === im.id ? updated : row)));
      } catch (e) {
        if (e instanceof AuthExpiredError) {
          window.location.reload();
          return;
        }
        setIms(previous);
        setError(`Couldn't move ${dealTitle(im)}: ${(e as Error).message}`);
      }
    },
    [ims, canEdit],
  );

  if (state === 'loading') {
    return <div className="card p-8 text-center text-sm text-ink-300">Loading the board…</div>;
  }
  if (state === 'error') {
    return (
      <div className="card p-8 text-center text-sm text-negative-500">
        {error ?? 'Something went wrong.'}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
        <div className="flex flex-wrap items-center gap-0.5 rounded-xl border border-white/[0.06] bg-ink-800/60 p-1">
          <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
            All deals
          </FilterChip>
          <FilterChip active={filter === 'mine'} onClick={() => setFilter('mine')}>
            Mine
          </FilterChip>
          <FilterChip active={filter === 'unclaimed'} onClick={() => setFilter('unclaimed')}>
            Unclaimed
            {unclaimedCount > 0 ? (
              <span className="rounded-full bg-accent-500/25 px-1.5 text-[10px] font-semibold text-accent-300">
                {unclaimedCount}
              </span>
            ) : null}
          </FilterChip>
          {team
            .filter((m) => m.email !== user.email.toLowerCase())
            .map((m) => (
              <FilterChip key={m.email} active={filter === m.email} onClick={() => setFilter(m.email)}>
                <OwnerBadge email={m.email} name={m.first_name} />
                {m.first_name}
              </FilterChip>
            ))}
        </div>
        <div className="flex items-center gap-3">
          <AgentStatus run={lastRun} />
          <span className="rounded-md bg-white/[0.04] px-2 py-1 tabular-nums text-ink-400">
            {visible.length === ims.length ? `${ims.length} deals` : `${visible.length} of ${ims.length} deals`}
          </span>
        </div>
      </div>

      {error && state === 'ready' ? (
        <div className="card border-negative-500/30 p-3 text-xs text-negative-500">{error}</div>
      ) : null}

      {/* One column per stage, side by side; the row scrolls sideways when the
          window is narrower than the pipeline, so names never get squeezed.
          The two dead ends fold down to a slim rail by default: they hold
          most of the deals but are only ever history. */}
      <div className="-mx-4 overflow-x-auto px-4 pb-2 sm:-mx-6 sm:px-6">
        {/* The board fills the window and each column scrolls on its own, so
            the stage headers always stay in view. */}
        <div className="flex h-[calc(100vh-10.5rem)] min-h-[28rem] gap-2.5">
          {STAGES.map((stage) => {
            const cards = byStage.get(stage) ?? [];
            const dead = isRejectedStage(stage);
            const folded = dead && !openDead.has(stage);
            const dropProps = {
              onDragOver: (e: React.DragEvent) => {
                // Without preventDefault the drop never fires.
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                setOverStage(stage);
              },
              onDragLeave: () => setOverStage((s) => (s === stage ? null : s)),
              onDrop: (e: React.DragEvent) => {
                e.preventDefault();
                setOverStage(null);
                const id = e.dataTransfer.getData('text/plain');
                const im = ims.find((row) => row.id === id);
                if (im) void move(im, stage);
              },
            };
            if (folded) {
              return (
                <button
                  key={stage}
                  type="button"
                  {...dropProps}
                  onClick={() => toggleDead(stage)}
                  title={`Show ${STAGE_LABELS[stage]}`}
                  className={`flex w-10 shrink-0 flex-col items-center gap-3 self-stretch rounded-xl border py-3 transition-colors ${
                    overStage === stage
                      ? 'border-accent-400/50 bg-accent-500/10'
                      : 'border-white/[0.05] bg-ink-900/40 hover:bg-white/[0.04]'
                  }`}
                >
                  <span className="rounded-md bg-white/[0.06] px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-ink-300">
                    {cards.length}
                  </span>
                  <span className="text-xs font-medium text-ink-400 [writing-mode:vertical-rl] rotate-180">
                    {STAGE_LABELS[stage]}
                  </span>
                </button>
              );
            }
            return (
              <section
                key={stage}
                {...dropProps}
                className={`flex w-[min(19rem,80vw)] shrink-0 flex-col overflow-hidden rounded-xl border transition-colors sm:w-auto ${
                  cards.length === 0 ? 'sm:min-w-[10.5rem] sm:flex-[0.6]' : 'sm:min-w-[12rem] sm:flex-1'
                } ${
                  overStage === stage
                    ? 'border-accent-400/50 bg-accent-500/[0.07]'
                    : 'border-white/[0.05] bg-ink-900/40'
                }`}
              >
                <header className="flex items-center justify-between gap-2 border-b border-white/[0.05] px-3 py-2.5">
                  <h2 className="flex min-w-0 items-center gap-2 text-[13px] font-semibold text-ink-100">
                    <span className={`h-2 w-2 shrink-0 rounded-full ${STAGE_TONE[stage].dot}`} />
                    <span className="truncate">{STAGE_LABELS[stage]}</span>
                  </h2>
                  <span className="flex items-center gap-1">
                    <span className="rounded-md bg-white/[0.06] px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-ink-300">
                      {cards.length}
                    </span>
                    {dead ? (
                      <button
                        type="button"
                        onClick={() => toggleDead(stage)}
                        title="Fold this column away"
                        className="rounded-md px-1.5 py-0.5 text-[11px] text-ink-400 hover:bg-white/[0.06] hover:text-ink-100"
                      >
                        Hide
                      </button>
                    ) : null}
                  </span>
                </header>
                <div className="flex-1 space-y-2 overflow-y-auto p-2">
                  {cards.map((im) => (
                    <Card
                      key={im.id}
                      im={im}
                      dragging={draggingId === im.id}
                      onDragStart={() => setDraggingId(im.id)}
                      onDragEnd={() => setDraggingId(null)}
                      onOpen={() => navigate(`/ims/${im.id}`)}
                      onClaim={() => void claim(im)}
                      claiming={claimingId === im.id}
                    />
                  ))}
                  {cards.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-white/[0.06] px-1 py-8 text-center text-[11px] text-ink-500">
                      No deals here
                    </p>
                  ) : null}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
